import { inject, Service, signal } from '@angular/core';
import {
  ExternalDocumentRequestOutput,
  GetInvestisseurDocumentRequestsForConsultantGQL,
  CreateDocumentRequestsInput,
  CreateDocumentRequestsGQL,
  CreateDocumentRequestOutput,
  DeleteDocumentRequestGQL,
  ValidateDocumentRequestOutput,
  ValidateDocumentRequestsGQL,
  ValidateDocumentRequestsInput,
  SendFollowupMailGQL,
  GetDocumentRequestCountsGQL,
  DocumentRequestCountsOutput,
  GetFilteredDocumentRequestPaginatedGQL,
  DocumentRequestSearchInput,
  PaginatedResultOfDocumentRequestsByInvestisseurOutput,
  GetDocumentRequestFilteringValuesGQL,
  DocumentRequestFilteringValuesOutput,
  PreviewDocumentEmailTemplateGQL,
  PreviewDocumentEmailTemplateInput,
  RequestInvestisseurAccountActivationGQL,
  RegenerateMagicLinkGQL,
  CreateBulkDocumentRequestsGQL,
  CreateBulkDocumentRequestsInput,
  BulkCreateDocumentRequestsOutput,
  SetInvestisseurNextAppointmentGQL,
} from '@lib/models/generated/myStellium-graphql';
import { lastValueFrom } from 'rxjs';

@Service()
export class MyStelliumService {
  private readonly getInvestisseurDocumentRequestsForConsultantGQL = inject(GetInvestisseurDocumentRequestsForConsultantGQL);
  private readonly createDocumentRequestsGQL = inject(CreateDocumentRequestsGQL);
  private readonly deleteDocumentRequestGQL = inject(DeleteDocumentRequestGQL);
  private readonly validateDocumentRequestsGQL = inject(ValidateDocumentRequestsGQL);
  private readonly sendFollowupMailGQL = inject(SendFollowupMailGQL);
  private readonly getDocumentRequestCountsGQL = inject(GetDocumentRequestCountsGQL);
  private readonly getFilteredDocumentRequestPaginatedGQL = inject(GetFilteredDocumentRequestPaginatedGQL);
  private readonly getDocumentRequestFilteringValuesGQL = inject(GetDocumentRequestFilteringValuesGQL);
  private readonly previewDocumentEmailTemplateGQL = inject(PreviewDocumentEmailTemplateGQL);
  private readonly requestInvestisseurAccountActivationGQL = inject(RequestInvestisseurAccountActivationGQL);
  private readonly regenerateMagicLinkGQL = inject(RegenerateMagicLinkGQL);
  private readonly createBulkDocumentRequestsGQL = inject(CreateBulkDocumentRequestsGQL);
  private readonly setInvestisseurNextAppointmentGQL = inject(SetInvestisseurNextAppointmentGQL);

  public readonly documentRequestData = signal<ExternalDocumentRequestOutput | null>(null);
  public readonly documentRequestLoading = signal(false);
  public readonly documentRequestLoadError = signal(false);
  private documentRequestInvestisseurId: number | null = null;

  clearDocumentRequestData(): void {
    this.documentRequestInvestisseurId = null;
    this.documentRequestData.set(null);
    this.documentRequestLoading.set(false);
    this.documentRequestLoadError.set(false);
  }

  async getInvestisseurDocumentRequestsForConsultant(investisseurId: number): Promise<void> {
    if (this.documentRequestInvestisseurId !== investisseurId) {
      this.documentRequestInvestisseurId = investisseurId;
      this.documentRequestData.set(null);
    }
    // Ignore les réponses arrivées après un changement d'investisseur ou un clear
    const isCurrentRequest = () => this.documentRequestInvestisseurId === investisseurId;
    this.documentRequestLoading.set(true);
    this.documentRequestLoadError.set(false);
    try {
      const result = await lastValueFrom(
        this.getInvestisseurDocumentRequestsForConsultantGQL.fetch({ fetchPolicy: 'network-only', variables: { investisseurId } })
      );

      if (result.error) {
        throw result.error;
      }

      const data = result.data?.investisseurDocumentRequestsForConsultant as ExternalDocumentRequestOutput | undefined;
      if (!data) {
        throw new Error('Le chargement des demandes de documents a échoué.');
      }

      if (isCurrentRequest()) {
        this.documentRequestData.set(data);
      }
    } catch (error) {
      if (isCurrentRequest()) {
        this.documentRequestLoadError.set(true);
      }
      throw error;
    } finally {
      if (isCurrentRequest()) {
        this.documentRequestLoading.set(false);
      }
    }
  }

  async createDocumentRequests(input: CreateDocumentRequestsInput): Promise<CreateDocumentRequestOutput | null> {
    const result = await lastValueFrom(this.createDocumentRequestsGQL.mutate({ variables: { input } }));
    const data = result.data?.createDocumentRequests;
    if (!data) {
      throw result.error ?? new Error('La création des demandes de documents a échoué.');
    }

    return data;
  }

  async createBulkDocumentRequests(input: CreateBulkDocumentRequestsInput): Promise<BulkCreateDocumentRequestsOutput> {
    const result = await lastValueFrom(this.createBulkDocumentRequestsGQL.mutate({ variables: { input } }));
    const data = result.data?.createBulkDocumentRequests;
    if (!data) {
      throw result.error ?? new Error('La création des demandes de documents groupées a échoué.');
    }

    return data;
  }

  async uploadDerDocument(uploadUrl: string, documentUrl: string): Promise<void> {
    const document = await fetch(documentUrl);
    if (!document.ok) {
      throw new Error('Le DER à transmettre est introuvable.');
    }
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'x-ms-blob-content-type': 'application/pdf' },
      body: await document.blob(),
    });
    if (!response.ok) {
      throw new Error(`L'envoi du DER a échoué (${response.status}).`);
    }
  }

  async deleteDocumentRequest(documentRequestId: number): Promise<boolean> {
    const result = await lastValueFrom(this.deleteDocumentRequestGQL.mutate({ variables: { documentRequestId } }));
    const data = result.data?.deleteDocumentRequest;
    if (data === null || data === undefined) {
      throw result.error ?? new Error('La suppression de la demande de documents a échoué.');
    }
    return data;
  }

  async validateDocumentRequests(input: ValidateDocumentRequestsInput): Promise<ValidateDocumentRequestOutput> {
    const result = await lastValueFrom(
      this.validateDocumentRequestsGQL.mutate({
        variables: { input },
      })
    );
    const data = result.data?.validateDocumentRequests;
    if (!data) {
      throw result.error ?? new Error('La validation des demandes de documents a échoué.');
    }
    return data;
  }

  async sendFollowupMail(investisseurId: number): Promise<boolean> {
    const result = await lastValueFrom(this.sendFollowupMailGQL.mutate({ variables: { investisseurId } }));
    const data = result.data?.sendFollowUpMail;
    if (data === null || data === undefined) {
      throw result.error ?? new Error("L'envoi du mail de relance a échoué.");
    }
    return data;
  }

  async requestInvestisseurAccountActivation(investisseurId: number): Promise<boolean> {
    const result = await lastValueFrom(this.requestInvestisseurAccountActivationGQL.mutate({ variables: { investisseurId } }));
    const data = result.data?.requestInvestisseurAccountActivation;
    if (data === null || data === undefined) {
      throw result.error ?? new Error("L'envoi de l'invitation a échoué.");
    }
    return data;
  }

  async setInvestisseurNextAppointment(investisseurId: number, nextAppointmentAt: string | null): Promise<void> {
    const result = await lastValueFrom(
      this.setInvestisseurNextAppointmentGQL.mutate({ variables: { input: { investisseurId, nextAppointmentAt } } })
    );
    const data = result.data?.setInvestisseurNextAppointment;
    if (data === null || data === undefined) {
      throw result.error ?? new Error("L'enregistrement du rendez-vous a échoué.");
    }

    if (this.documentRequestInvestisseurId === investisseurId) {
      this.documentRequestData.update(documentRequestData =>
        documentRequestData ? { ...documentRequestData, nextAppointmentAt } : documentRequestData
      );
    }
  }

  async regenerateMagicLink(investisseurId: number, notifyInvestor: boolean): Promise<CreateDocumentRequestOutput> {
    const result = await lastValueFrom(this.regenerateMagicLinkGQL.mutate({ variables: { input: { investisseurId, notifyInvestor } } }));
    const data = result.data?.regenerateMagicLink;
    if (!data) {
      throw result.error ?? new Error('Le renvoi du lien de dépôt a échoué.');
    }
    return data;
  }

  async getDocumentRequestCounts(): Promise<DocumentRequestCountsOutput> {
    const result = await lastValueFrom(this.getDocumentRequestCountsGQL.fetch({ fetchPolicy: 'network-only' }));
    const data = result.data?.documentRequestCounts;
    if (!data) {
      throw result.error ?? new Error('La récupération des comptes des demandes de documents a échoué.');
    }
    return data;
  }

  async getDocumentRequestFilteringValues(): Promise<DocumentRequestFilteringValuesOutput> {
    const result = await lastValueFrom(this.getDocumentRequestFilteringValuesGQL.fetch());
    const data = result.data?.documentRequestFilteringValues;
    if (!data) {
      throw result.error ?? new Error('La récupération des valeurs de filtrage des demandes de documents a échoué.');
    }
    return data;
  }

  async previewDocumentEmailTemplate(input: PreviewDocumentEmailTemplateInput): Promise<string | null> {
    const result = await lastValueFrom(this.previewDocumentEmailTemplateGQL.fetch({ fetchPolicy: 'network-only', variables: { input } }));
    if (result.error) {
      throw result.error;
    }
    return result.data?.previewDocumentEmailTemplate.html ?? null;
  }

  async getFilteredDocumentRequestPaginated(
    searchInput: DocumentRequestSearchInput
  ): Promise<PaginatedResultOfDocumentRequestsByInvestisseurOutput> {
    const result = await lastValueFrom(
      this.getFilteredDocumentRequestPaginatedGQL.fetch({ variables: { searchInput }, fetchPolicy: 'network-only' })
    );
    const data = result.data?.filteredDocumentRequestPaginated;
    if (!data) {
      throw result.error ?? new Error('La récupération des demandes de documents filtrées a échoué.');
    }
    return data as PaginatedResultOfDocumentRequestsByInvestisseurOutput;
  }
}
